import { Fullscreen, Pause, PlayArrow, VolumeOff, VolumeUp } from "@mui/icons-material";
import { Box, Card, CardContent, IconButton, LinearProgress, Slider, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
  src: string;
  title?: string;
  poster?: string;
  autoPlay?: boolean;
  loop?: boolean;
  controls?: boolean;
  width?: string | number;
  height?: string | number;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  title,
  poster,
  autoPlay = false,
  loop = false,
  controls = true,
  width = "100%",
  height = "auto",
  onPlay,
  onPause,
  onEnded
}) => {
  const [playing, setPlaying] = useState<boolean>(autoPlay);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(0.7);
  const [muted, setMuted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [buffered, setBuffered] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      if (autoPlay) {
        videoRef.current.play().catch(() => {
          // Autoplay was prevented
          setPlaying(false);
        });
      }
    }
  }, [autoPlay]);

  const handlePlayPause = (): void => {
    if (!videoRef.current) return;

    if (playing) {
      videoRef.current.pause();
      onPause?.();
    } else {
      videoRef.current.play().catch(() => {
        // Play was prevented
        setPlaying(false);
      });
      onPlay?.();
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = (): void => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);

    // Update buffered amount
    if (videoRef.current.buffered.length > 0) {
      setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
    }
  };

  const handleLoadedMetadata = (): void => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
    setLoading(false);
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]): void => {
    if (!videoRef.current) return;
    const newVolume = newValue as number;
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
  };

  const handleMuteToggle = (): void => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const handleSeek = (_event: Event, newValue: number | number[]): void => {
    if (!videoRef.current) return;
    const newTime = newValue as number;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleFullscreen = (): void => {
    if (!videoRef.current) return;
    const current: any = videoRef.current;
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    } else if (current.webkitRequestFullscreen) {
      current.webkitRequestFullscreen();
    } else if (current.msRequestFullscreen) {
      current.msRequestFullscreen();
    }
  };

  const handleVideoEnded = (): void => {
    setPlaying(false);
    onEnded?.();
  };

  // Format time in MM:SS
  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <Card sx={{ maxWidth: 600 }}>
      <Box sx={{ position: "relative" }}>
        {loading && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              width: "100%",
              zIndex: 1
            }}
          />
        )}
        <video
          ref={videoRef}
          width={width}
          height={height}
          poster={poster}
          loop={loop}
          muted={muted}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleVideoEnded}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          style={{ display: "block" }}
        >
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Box>

      {controls && (
        <CardContent>
          {title && (
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
          )}

          <Box sx={{ my: 1 }}>
            <Box sx={{ position: "relative", height: 8 }}>
              {/* Buffered progress */}
              <Slider
                value={buffered}
                max={duration || 100}
                disabled
                aria-label="buffered progress"
                size="small"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 8,
                  "& .MuiSlider-track": {
                    backgroundColor: "rgba(144, 202, 249, 0.5)"
                  },
                  "& .MuiSlider-thumb": {
                    display: "none"
                  }
                }}
              />

              {/* Playback progress */}
              <Slider
                value={currentTime}
                max={duration || 100}
                onChange={handleSeek}
                aria-label="video progress"
                size="small"
                sx={{ height: 8 }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="caption">{formatTime(currentTime)}</Typography>
              <Typography variant="caption">{formatTime(duration)}</Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={handlePlayPause} size="small">
              {playing ? <Pause /> : <PlayArrow />}
            </IconButton>

            <IconButton onClick={handleMuteToggle} size="small">
              {muted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>

            <Slider
              sx={{ mx: 1, width: 100 }}
              value={volume}
              onChange={handleVolumeChange}
              min={0}
              max={1}
              step={0.01}
              aria-label="Volume"
              size="small"
              disabled={muted}
            />

            <Box sx={{ flexGrow: 1 }} />

            <IconButton onClick={handleFullscreen} size="small">
              <Fullscreen />
            </IconButton>
          </Stack>
        </CardContent>
      )}
    </Card>
  );
};

export default VideoPlayer;
